import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import SearchableDropdown from '../SearchableDropdown';
import { SortableTh } from '../SortableTh';
import { useAuth } from '../../context/AuthContext';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import { formatDateTimePT } from '../../utils/timezone';
import { dateTimeLocalToIso, isoToDateTimeLocal } from '../../utils/dateTimeInput';
import {
  addPhoneCallNote,
  createPhoneCall,
  fetchPhoneCallNotes,
  fetchPhoneCalls,
  logScheduledCall,
  unvoidPhoneCall,
  updatePhoneCall,
  voidPhoneCall,
  type PhoneCallNoteRow,
  type PhoneCallRow,
  type UpdatePhoneCallPayload,
} from '../../api/phoneCalls';
import { updateClient, type ClientPickerRow } from '../../api/clients';
import { useClientPickerOptions } from '../../hooks/useClientPickerOptions';
import { fetchVerifiedUsers, type VerifiedUser } from '../../api/users';

function when(iso: string | null): string {
  return formatDateTimePT(iso, { second: undefined });
}

type CallColumn = {
  label: string;
  key: string;
  render: (r: PhoneCallRow) => React.ReactNode;
  /** Value used for sorting; falls back to the rendered text. */
  sortValue?: (r: PhoneCallRow) => string;
};

function CallsTable({
  title,
  sectionId,
  rows,
  emptyText,
  columns,
  actions,
}: {
  title: string;
  sectionId: string;
  rows: PhoneCallRow[];
  emptyText: string;
  columns: CallColumn[];
  actions?: (r: PhoneCallRow) => React.ReactNode;
}) {
  const headingId = `call-log-section-${sectionId}`;
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, (r, key) => {
        const col = columns.find((c) => c.key === key);
        if (!col) return '';
        if (col.sortValue) return col.sortValue(r);
        const rendered = col.render(r);
        return typeof rendered === 'string' ? rendered : '';
      }),
    [rows, sortKey, sortDir, columns]
  );
  const colCount = columns.length + (actions ? 1 : 0);
  return (
    <section className="events-table-section" aria-labelledby={headingId}>
      <h2 className="events-section-title" id={headingId}>
        {title} <span className="events-section-count">({rows.length})</span>
      </h2>
      <div className="events-table-wrap">
        <table className="tasks-search-results-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <SortableTh
                  key={c.key}
                  label={c.label}
                  columnKey={c.key}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSort={toggleSort}
                />
              ))}
              {actions ? <th scope="col">Actions</th> : null}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="events-empty-cell">
                  {emptyText}
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr key={r.id}>
                  {columns.map((c) => (
                    <td key={c.key} data-label={c.label}>
                      {c.render(r)}
                    </td>
                  ))}
                  {actions ? (
                    <td data-label="Actions">
                      <div className="call-log-row-actions">{actions(r)}</div>
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type CompanySource = 'client' | 'manual';

/**
 * Steps of the Schedule a Call flow. Picking an existing client routes through
 * 'confirm' so the stored contact details are eyeballed before the call is
 * booked; editing them then offers to write the corrections back to the client.
 */
type ScheduleStep = 'form' | 'confirm' | 'edit' | 'sync-client';

const CallLogTab: React.FC = () => {
  const { user } = useAuth();

  const [scheduled, setScheduled] = useState<PhoneCallRow[]>([]);
  const [logged, setLogged] = useState<PhoneCallRow[]>([]);
  const [voided, setVoided] = useState<PhoneCallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const { clients, clientsLoading, setClients } = useClientPickerOptions();

  // Shared company/contact fields, used by both the log and schedule modals.
  const [employeeName, setEmployeeName] = useState('');
  const [source, setSource] = useState<CompanySource>('client');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [location, setLocation] = useState('');
  const [pointOfContact, setPointOfContact] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [spokeWith, setSpokeWith] = useState('');
  const [callAt, setCallAt] = useState('');

  const [logOpen, setLogOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleStep, setScheduleStep] = useState<ScheduleStep>('form');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Logging / voiding a row from the Scheduled table.
  const [rowCall, setRowCall] = useState<PhoneCallRow | null>(null);
  const [rowLogOpen, setRowLogOpen] = useState(false);
  const [rowVoidOpen, setRowVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  // Editing a call from the Scheduled or Logged table.
  const [editCall, setEditCall] = useState<PhoneCallRow | null>(null);

  // Follow-up notes on a logged call.
  const [notesCall, setNotesCall] = useState<PhoneCallRow | null>(null);
  const [callNotes, setCallNotes] = useState<PhoneCallNoteRow[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchPhoneCalls();
      setScheduled(data.scheduled);
      setLogged(data.logged);
      setVoided(data.void);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load calls.');
      setScheduled([]);
      setLogged([]);
      setVoided([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    void fetchVerifiedUsers()
      .then((users) => {
        if (!cancelled) setVerifiedUsers(users);
      })
      .catch(() => {
        if (!cancelled) setVerifiedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const notesCallId = notesCall?.id ?? null;
  useEffect(() => {
    if (notesCallId == null) return;
    let cancelled = false;
    void fetchPhoneCallNotes(notesCallId)
      .then((rows) => {
        if (!cancelled) setCallNotes(rows);
      })
      .catch((e) => {
        if (!cancelled) setFormError(e instanceof Error ? e.message : 'Failed to load notes.');
      })
      .finally(() => {
        if (!cancelled) setNotesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [notesCallId]);

  const employeeOptions = useMemo(() => verifiedUsers.map((u) => u.username), [verifiedUsers]);
  const employeeUserId = useMemo(
    () => verifiedUsers.find((u) => u.username === employeeName)?.id ?? null,
    [verifiedUsers, employeeName]
  );
  const clientOptions = useMemo(() => clients.map((c) => c.company), [clients]);
  const selectedClientDisplay = useMemo(
    () => clients.find((c) => c.id === selectedClientId)?.company ?? '',
    [clients, selectedClientId]
  );

  const resetForm = useCallback(() => {
    setEmployeeName(user?.username ?? '');
    setSource('client');
    setSelectedClientId(null);
    setCompanyName('');
    setLocation('');
    setPointOfContact('');
    setContactPhone('');
    setContactEmail('');
    setReason('');
    setNotes('');
    setSpokeWith('');
    setCallAt('');
    setFormError('');
  }, [user?.username]);

  const applyClient = (client: ClientPickerRow | null) => {
    if (!client) {
      setSelectedClientId(null);
      return;
    }
    setSelectedClientId(client.id);
    setCompanyName(client.company ?? '');
    setLocation(client.site_location || client.billing_address || '');
    setPointOfContact(client.point_of_contact ?? '');
    setContactPhone(client.contact_phone ?? '');
    setContactEmail(client.contact_email ?? '');
    setFormError('');
  };

  const openLog = () => {
    resetForm();
    setLogOpen(true);
  };

  const openSchedule = () => {
    resetForm();
    setScheduleStep('form');
    setScheduleOpen(true);
  };

  /** Shared validation for both modals; returns an error message or ''. */
  const validateShared = (): string => {
    if (!employeeUserId) return 'Select the employee for this call.';
    if (source === 'client' && selectedClientId == null) return 'Select a client, or enter the company information.';
    if (source === 'manual' && !companyName.trim()) return 'Company name is required.';
    if (!callAt) return 'A date and time is required.';
    if (!reason.trim()) return 'A reason for the call is required.';
    return '';
  };

  const buildPayload = () => ({
    user_id: user!.id,
    employee_user_id: employeeUserId!,
    client_id: source === 'client' ? selectedClientId : null,
    company_name: companyName.trim() || undefined,
    location: location.trim() || null,
    point_of_contact: pointOfContact.trim() || null,
    contact_phone: contactPhone.trim() || null,
    contact_email: contactEmail.trim() || null,
    reason: reason.trim() || null,
    notes: notes.trim() || null,
  });

  const submitLog = async () => {
    if (!user?.id) return;
    const problem = validateShared();
    if (problem) {
      setFormError(problem);
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await createPhoneCall({
        ...buildPayload(),
        status: 'Logged',
        spoke_with: spokeWith.trim() || null,
        occurred_at: dateTimeLocalToIso(callAt),
      });
      setLogOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to log the call.');
    } finally {
      setSubmitting(false);
    }
  };

  const createScheduledCall = async () => {
    if (!user?.id) return;
    setSubmitting(true);
    setFormError('');
    try {
      await createPhoneCall({
        ...buildPayload(),
        status: 'Scheduled',
        scheduled_at: dateTimeLocalToIso(callAt),
      });
      setScheduleOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to schedule the call.');
    } finally {
      setSubmitting(false);
    }
  };

  /** Step 1 -> either the confirmation step (existing client) or straight to booking. */
  const submitScheduleForm = async () => {
    const problem = validateShared();
    if (problem) {
      setFormError(problem);
      return;
    }
    if (source === 'client') {
      setScheduleStep('confirm');
      return;
    }
    await createScheduledCall();
  };

  /** After editing the contact details, offer to write them back to the client. */
  const submitScheduleEdit = () => {
    if (!companyName.trim()) {
      setFormError('Company name is required.');
      return;
    }
    setFormError('');
    setScheduleStep(selectedClientId != null ? 'sync-client' : 'confirm');
  };

  const finishWithClientSync = async (syncToClient: boolean) => {
    if (syncToClient && selectedClientId != null) {
      try {
        await updateClient(selectedClientId, {
          company: companyName.trim(),
          point_of_contact: pointOfContact.trim() || undefined,
          contact_phone: contactPhone.trim() || null,
          contact_email: contactEmail.trim() || null,
          site_location: location.trim() || null,
        });
        setClients((prev) =>
          prev.map((c) =>
            c.id === selectedClientId
              ? {
                  ...c,
                  company: companyName.trim(),
                  point_of_contact: pointOfContact.trim(),
                  contact_phone: contactPhone.trim() || null,
                  contact_email: contactEmail.trim() || null,
                  site_location: location.trim() || null,
                }
              : c
          )
        );
      } catch (e) {
        setFormError(e instanceof Error ? e.message : 'Failed to update the client.');
        return;
      }
    }
    await createScheduledCall();
  };

  const openRowLog = (call: PhoneCallRow) => {
    setRowCall(call);
    setSpokeWith(call.spoke_with ?? '');
    setNotes(call.notes ?? '');
    setCallAt(isoToDateTimeLocal(call.scheduled_at));
    setFormError('');
    setRowLogOpen(true);
  };

  const submitRowLog = async () => {
    if (!rowCall) return;
    setSubmitting(true);
    setFormError('');
    try {
      await logScheduledCall(rowCall.id, {
        occurred_at: dateTimeLocalToIso(callAt),
        spoke_with: spokeWith.trim() || null,
        notes: notes.trim() || null,
      });
      setRowLogOpen(false);
      setRowCall(null);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to log the call.');
    } finally {
      setSubmitting(false);
    }
  };

  const openRowVoid = (call: PhoneCallRow) => {
    setRowCall(call);
    setVoidReason('');
    setFormError('');
    setRowVoidOpen(true);
  };

  const submitRowVoid = async () => {
    if (!rowCall) return;
    if (!voidReason.trim()) {
      setFormError('A reason is required to void a call.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await voidPhoneCall(rowCall.id, voidReason.trim());
      setRowVoidOpen(false);
      setRowCall(null);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to void the call.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnvoid = async (call: PhoneCallRow) => {
    setSubmitting(true);
    try {
      await unvoidPhoneCall(call.id);
      await load();
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to restore the call.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (call: PhoneCallRow) => {
    setEditCall(call);
    setEmployeeName(call.employee_name ?? '');
    setCompanyName(call.company_name);
    setLocation(call.location ?? '');
    setPointOfContact(call.point_of_contact ?? '');
    setContactPhone(call.contact_phone ?? '');
    setContactEmail(call.contact_email ?? '');
    setSpokeWith(call.spoke_with ?? '');
    setReason(call.reason ?? '');
    setNotes(call.notes ?? '');
    setCallAt(isoToDateTimeLocal(call.status === 'Logged' ? call.occurred_at : call.scheduled_at));
    setFormError('');
  };

  const submitEdit = async () => {
    if (!editCall) return;
    const isLogged = editCall.status === 'Logged';
    // Only a changed employee has to be picked from the list, so a call whose
    // employee has since been deactivated can still be edited.
    const employeeChanged = employeeName !== (editCall.employee_name ?? '');
    let problem = '';
    if (employeeChanged && !employeeUserId) problem = 'Select the employee for this call.';
    else if (!companyName.trim()) problem = 'Company name is required.';
    else if (!callAt) problem = 'A date and time is required.';
    else if (!reason.trim()) problem = 'A reason for the call is required.';
    if (problem) {
      setFormError(problem);
      return;
    }

    const payload: UpdatePhoneCallPayload = {
      company_name: companyName.trim(),
      location: location.trim() || null,
      point_of_contact: pointOfContact.trim() || null,
      contact_phone: contactPhone.trim() || null,
      contact_email: contactEmail.trim() || null,
      reason: reason.trim() || null,
      notes: notes.trim() || null,
    };
    if (employeeChanged) payload.employee_user_id = employeeUserId;
    if (isLogged) payload.spoke_with = spokeWith.trim() || null;
    // The server treats any scheduled_at it receives as a reschedule and re-arms
    // the one-hour reminder, so the time is only sent when it changed.
    if (callAt !== isoToDateTimeLocal(isLogged ? editCall.occurred_at : editCall.scheduled_at)) {
      payload[isLogged ? 'occurred_at' : 'scheduled_at'] = dateTimeLocalToIso(callAt);
    }

    setSubmitting(true);
    setFormError('');
    try {
      await updatePhoneCall(editCall.id, payload);
      setEditCall(null);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save the call.');
    } finally {
      setSubmitting(false);
    }
  };

  const openNotes = (call: PhoneCallRow) => {
    setNotesCall(call);
    setCallNotes([]);
    setNotesLoading(true);
    setNoteDraft('');
    setFormError('');
  };

  const submitNote = async () => {
    if (!notesCall || !user?.id) return;
    const text = noteDraft.trim();
    if (!text) return;
    setSubmitting(true);
    setFormError('');
    try {
      const note = await addPhoneCallNote(notesCall.id, text, user.id);
      setCallNotes((prev) => [...prev, note]);
      setNoteDraft('');
      setLogged((prev) =>
        prev.map((r) => (r.id === notesCall.id ? { ...r, note_count: (r.note_count ?? 0) + 1 } : r))
      );
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to add the note.');
    } finally {
      setSubmitting(false);
    }
  };

  const employeeCol: CallColumn = {
    label: 'Employee',
    key: 'employee',
    render: (r) => r.employee_name || '—',
  };
  const companyCol: CallColumn = { label: 'Company', key: 'company', render: (r) => r.company_name };
  const reasonCol: CallColumn = { label: 'Reason', key: 'reason', render: (r) => r.reason || '—' };
  const notesCol: CallColumn = { label: 'Notes', key: 'notes', render: (r) => r.notes || '—' };

  const scheduledColumns: CallColumn[] = [
    employeeCol,
    companyCol,
    { label: 'Point of contact', key: 'poc', render: (r) => r.point_of_contact || '—' },
    { label: 'Phone', key: 'phone', render: (r) => r.contact_phone || '—' },
    {
      label: 'Scheduled for',
      key: 'scheduled_at',
      render: (r) => when(r.scheduled_at),
      sortValue: (r) => r.scheduled_at ?? '',
    },
    reasonCol,
  ];

  const loggedColumns: CallColumn[] = [
    employeeCol,
    companyCol,
    { label: 'Spoke with', key: 'spoke_with', render: (r) => r.spoke_with || '—' },
    {
      label: 'Occurred',
      key: 'occurred_at',
      render: (r) => when(r.occurred_at),
      sortValue: (r) => r.occurred_at ?? '',
    },
    reasonCol,
    notesCol,
  ];

  const voidColumns: CallColumn[] = [
    employeeCol,
    companyCol,
    {
      label: 'Was scheduled for',
      key: 'scheduled_at',
      render: (r) => when(r.scheduled_at),
      sortValue: (r) => r.scheduled_at ?? '',
    },
    reasonCol,
    { label: 'Void reason', key: 'void_reason', render: (r) => r.void_reason || '—' },
  ];

  const contactSummary = (
    <dl className="call-log-summary">
      <div>
        <dt>Company</dt>
        <dd>{companyName || '—'}</dd>
      </div>
      <div>
        <dt>Location</dt>
        <dd>{location || '—'}</dd>
      </div>
      <div>
        <dt>Point of contact</dt>
        <dd>{pointOfContact || '—'}</dd>
      </div>
      <div>
        <dt>Phone number</dt>
        <dd>{contactPhone || '—'}</dd>
      </div>
      <div>
        <dt>Email address</dt>
        <dd>{contactEmail || '—'}</dd>
      </div>
    </dl>
  );

  /** Company/contact inputs, shared by the log modal and the schedule edit step. */
  const contactFields = (idPrefix: string) => (
    <>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-company`} className="form-label">
          Company
        </label>
        <input
          id={`${idPrefix}-company`}
          type="text"
          className="form-input"
          value={companyName}
          onChange={(e) => {
            setCompanyName(e.target.value);
            setFormError('');
          }}
          placeholder="e.g. Grand Plaza Hotel"
          disabled={submitting}
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-location`} className="form-label">
          Location
        </label>
        <input
          id={`${idPrefix}-location`}
          type="text"
          className="form-input"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Street, City, State, ZIP"
          disabled={submitting}
        />
      </div>
      <div className="elevators-form-row">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-poc`} className="form-label">
            Point of Contact
          </label>
          <input
            id={`${idPrefix}-poc`}
            type="text"
            className="form-input"
            value={pointOfContact}
            onChange={(e) => setPointOfContact(e.target.value)}
            disabled={submitting}
          />
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-phone`} className="form-label">
            Phone Number
          </label>
          <input
            id={`${idPrefix}-phone`}
            type="tel"
            className="form-input"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-email`} className="form-label">
          Email Address
        </label>
        <input
          id={`${idPrefix}-email`}
          type="email"
          className="form-input"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          disabled={submitting}
        />
      </div>
    </>
  );

  const employeeField = (idPrefix: string, label: string) => (
    <div className="form-group">
      <label htmlFor={`${idPrefix}-employee`} className="form-label">
        {label}
      </label>
      <SearchableDropdown
        options={employeeOptions}
        value={employeeName}
        onChange={(display) => {
          setEmployeeName(display);
          setFormError('');
        }}
        placeholder="Select employee..."
        disabled={submitting}
      />
    </div>
  );

  const companySourceField = (namePrefix: string) => (
    <>
      <div className="form-group">
        <span className="form-label">Company</span>
        <div className="elevators-source-row">
          <label className="elevators-radio-label">
            <input
              type="radio"
              name={`${namePrefix}-source`}
              checked={source === 'client'}
              onChange={() => setSource('client')}
              disabled={submitting}
            />
            Select a client from the database
          </label>
          <label className="elevators-radio-label">
            <input
              type="radio"
              name={`${namePrefix}-source`}
              checked={source === 'manual'}
              onChange={() => {
                setSource('manual');
                setSelectedClientId(null);
              }}
              disabled={submitting}
            />
            Enter the company information
          </label>
        </div>
      </div>
      {source === 'client' ? (
        <div className="form-group">
          <span className="form-label">Client</span>
          <SearchableDropdown
            options={clientOptions}
            value={selectedClientDisplay}
            onChange={(display) => applyClient(clients.find((c) => c.company === display) ?? null)}
            placeholder={clientsLoading ? 'Loading clients…' : 'Type to search clients…'}
            disabled={submitting || clientsLoading}
            noResultsMessage="No matching client"
          />
        </div>
      ) : null}
    </>
  );

  const errorBlock = formError ? (
    <p className="create-order-error" role="alert">
      {formError}
    </p>
  ) : null;

  return (
    <>
      <div className="page-toolbar events-page-toolbar">
        <div className="events-toolbar-spacer" aria-hidden="true" />
        <div className="call-log-toolbar-actions">
          <button type="button" className="page-toolbar-btn" onClick={openLog}>
            <span aria-hidden="true">+</span> Log a Call
          </button>
          <button type="button" className="page-toolbar-btn" onClick={openSchedule}>
            <span aria-hidden="true">+</span> Schedule a Call
          </button>
        </div>
      </div>

      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading calls…</p>
      ) : (
        <>
          <CallsTable
            title="Scheduled Calls"
            sectionId="scheduled"
            rows={scheduled}
            emptyText="No scheduled calls."
            columns={scheduledColumns}
            actions={(r) => (
              <>
                <button type="button" className="elevator-btn" disabled={submitting} onClick={() => openRowLog(r)}>
                  Log a call
                </button>
                <button type="button" className="elevator-btn" disabled={submitting} onClick={() => openEdit(r)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="elevator-btn elevator-btn--void"
                  disabled={submitting}
                  onClick={() => openRowVoid(r)}
                >
                  Void
                </button>
              </>
            )}
          />
          <CallsTable
            title="Logged Calls"
            sectionId="logged"
            rows={logged}
            emptyText="No logged calls."
            columns={loggedColumns}
            actions={(r) => (
              <>
                <button type="button" className="elevator-btn" disabled={submitting} onClick={() => openEdit(r)}>
                  Edit
                </button>
                <button type="button" className="elevator-btn" disabled={submitting} onClick={() => openNotes(r)}>
                  Notes{r.note_count ? ` (${r.note_count})` : ''}
                </button>
                <button
                  type="button"
                  className="elevator-btn elevator-btn--void"
                  disabled={submitting}
                  onClick={() => openRowVoid(r)}
                >
                  Void
                </button>
              </>
            )}
          />
          <CallsTable
            title="Voided Calls"
            sectionId="void"
            rows={voided}
            emptyText="No voided calls."
            columns={voidColumns}
            actions={(r) => (
              <button type="button" className="elevator-btn" disabled={submitting} onClick={() => void handleUnvoid(r)}>
                Unvoid
              </button>
            )}
          />
        </>
      )}

      {/* Log a call that already happened */}
      <Modal
        isOpen={logOpen}
        onClose={() => setLogOpen(false)}
        title="Log a call"
        wide
        contentClassName="call-log-modal"
      >
        <div className="modal-body">
          {employeeField('log-call', 'Which employee made the call?')}
          {companySourceField('log-call')}
          {source === 'manual' ? contactFields('log-call') : null}
          {source === 'client' && selectedClientId != null ? contactSummary : null}
          <div className="form-group">
            <label htmlFor="log-call-spoke-with" className="form-label">
              Who did the employee talk to?
            </label>
            <input
              id="log-call-spoke-with"
              type="text"
              className="form-input"
              value={spokeWith}
              onChange={(e) => setSpokeWith(e.target.value)}
              placeholder="Full name"
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="log-call-at" className="form-label">
              When did the call occur?
            </label>
            <input
              id="log-call-at"
              type="datetime-local"
              className="form-input"
              value={callAt}
              onChange={(e) => {
                setCallAt(e.target.value);
                setFormError('');
              }}
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="log-call-reason" className="form-label">
              Reason for the call
            </label>
            <textarea
              id="log-call-reason"
              className="form-textarea"
              rows={2}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setFormError('');
              }}
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="log-call-notes" className="form-label">
              Additional notes
            </label>
            <textarea
              id="log-call-notes"
              className="form-textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
            />
          </div>
          {errorBlock}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={submitting} onClick={() => setLogOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={submitting} onClick={() => void submitLog()}>
            {submitting ? 'Logging…' : 'Log call'}
          </button>
        </div>
      </Modal>

      {/* Schedule a call: form -> confirm -> (edit -> sync) */}
      <Modal
        isOpen={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        title={
          scheduleStep === 'confirm'
            ? 'Is this information correct?'
            : scheduleStep === 'edit'
            ? 'Edit company information'
            : scheduleStep === 'sync-client'
            ? 'Update the client record?'
            : 'Schedule a call'
        }
        wide
        contentClassName="call-log-modal"
      >
        {scheduleStep === 'form' ? (
          <>
            <div className="modal-body">
              {employeeField('schedule-call', 'Assign an employee')}
              {companySourceField('schedule-call')}
              {source === 'manual' ? contactFields('schedule-call') : null}
              <div className="form-group">
                <label htmlFor="schedule-call-at" className="form-label">
                  Date &amp; time of the call
                </label>
                <input
                  id="schedule-call-at"
                  type="datetime-local"
                  className="form-input"
                  value={callAt}
                  onChange={(e) => {
                    setCallAt(e.target.value);
                    setFormError('');
                  }}
                  disabled={submitting}
                />
                <p className="elevators-confirm-hint">
                  The assigned employee gets an email reminder about an hour before the call.
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="schedule-call-reason" className="form-label">
                  Reason for the call
                </label>
                <textarea
                  id="schedule-call-reason"
                  className="form-textarea"
                  rows={2}
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setFormError('');
                  }}
                  disabled={submitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="schedule-call-notes" className="form-label">
                  Notes
                </label>
                <textarea
                  id="schedule-call-notes"
                  className="form-textarea"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={submitting}
                />
              </div>
              {errorBlock}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                disabled={submitting}
                onClick={() => setScheduleOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="update-button"
                disabled={submitting}
                onClick={() => void submitScheduleForm()}
              >
                {source === 'client' ? 'Continue' : submitting ? 'Scheduling…' : 'Schedule call'}
              </button>
            </div>
          </>
        ) : null}

        {scheduleStep === 'confirm' ? (
          <>
            <div className="modal-body">
              <p className="elevators-confirm-hint" style={{ marginTop: 0 }}>
                Confirm the contact information we have on file for this client before scheduling the call.
              </p>
              {contactSummary}
              <div className="form-group">
                <label htmlFor="schedule-confirm-notes" className="form-label">
                  Notes
                </label>
                <textarea
                  id="schedule-confirm-notes"
                  className="form-textarea"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={submitting}
                />
              </div>
              {errorBlock}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                disabled={submitting}
                onClick={() => {
                  // Declining sends them back to pick a different client.
                  setScheduleStep('form');
                  setSelectedClientId(null);
                }}
              >
                Decline
              </button>
              <button type="button" className="elevator-btn" disabled={submitting} onClick={() => setScheduleStep('edit')}>
                Edit
              </button>
              <button
                type="button"
                className="update-button"
                disabled={submitting}
                onClick={() => void createScheduledCall()}
              >
                {submitting ? 'Scheduling…' : 'Confirm & schedule'}
              </button>
            </div>
          </>
        ) : null}

        {scheduleStep === 'edit' ? (
          <>
            <div className="modal-body">
              {contactFields('schedule-edit')}
              {errorBlock}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                disabled={submitting}
                onClick={() => setScheduleStep('confirm')}
              >
                Back
              </button>
              <button type="button" className="update-button" disabled={submitting} onClick={submitScheduleEdit}>
                Submit changes
              </button>
            </div>
          </>
        ) : null}

        {scheduleStep === 'sync-client' ? (
          <>
            <div className="modal-body">
              <p style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.9375rem' }}>
                Do you also want to update this information on the client page? Either way the call will be scheduled
                with the details you entered.
              </p>
              {contactSummary}
              {errorBlock}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel-button"
                disabled={submitting}
                onClick={() => void finishWithClientSync(false)}
              >
                No, just this call
              </button>
              <button
                type="button"
                className="update-button"
                disabled={submitting}
                onClick={() => void finishWithClientSync(true)}
              >
                {submitting ? 'Saving…' : 'Yes, update the client'}
              </button>
            </div>
          </>
        ) : null}
      </Modal>

      {/* Log a scheduled call from the table */}
      <Modal
        isOpen={rowLogOpen}
        onClose={() => setRowLogOpen(false)}
        title="Log this call"
        narrow
        contentClassName="call-log-modal"
      >
        <div className="modal-body">
          <p style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.9375rem' }}>
            Confirm the call to <strong>{rowCall?.company_name}</strong> was made. Add any notes before confirming.
          </p>
          <div className="form-group">
            <label htmlFor="row-log-at" className="form-label">
              When did the call occur?
            </label>
            <input
              id="row-log-at"
              type="datetime-local"
              className="form-input"
              value={callAt}
              onChange={(e) => setCallAt(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="row-log-spoke-with" className="form-label">
              Who did the employee talk to?
            </label>
            <input
              id="row-log-spoke-with"
              type="text"
              className="form-input"
              value={spokeWith}
              onChange={(e) => setSpokeWith(e.target.value)}
              placeholder="Full name"
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="row-log-notes" className="form-label">
              Notes
            </label>
            <textarea
              id="row-log-notes"
              className="form-textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
            />
          </div>
          {errorBlock}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={submitting} onClick={() => setRowLogOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={submitting} onClick={() => void submitRowLog()}>
            {submitting ? 'Confirming…' : 'Confirm call was made'}
          </button>
        </div>
      </Modal>

      {/* Void a call */}
      <Modal
        isOpen={rowVoidOpen}
        onClose={() => setRowVoidOpen(false)}
        title="Void this call"
        narrow
        contentClassName="call-log-modal"
      >
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="row-void-reason" className="form-label">
              Reason for voiding
            </label>
            <textarea
              id="row-void-reason"
              className="form-textarea"
              rows={3}
              value={voidReason}
              onChange={(e) => {
                setVoidReason(e.target.value);
                setFormError('');
              }}
              placeholder="e.g. Client rescheduled indefinitely"
              disabled={submitting}
            />
          </div>
          {errorBlock}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={submitting} onClick={() => setRowVoidOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="elevator-btn elevator-btn--void"
            disabled={submitting}
            onClick={() => void submitRowVoid()}
          >
            {submitting ? 'Voiding…' : 'Void call'}
          </button>
        </div>
      </Modal>

      {/* Edit a scheduled or logged call */}
      <Modal
        isOpen={editCall != null}
        onClose={() => setEditCall(null)}
        title="Edit call"
        wide
        contentClassName="call-log-modal"
      >
        <div className="modal-body">
          {employeeField(
            'edit-call',
            editCall?.status === 'Logged' ? 'Which employee made the call?' : 'Assigned employee'
          )}
          {editCall?.client_id != null ? (
            <p className="elevators-confirm-hint" style={{ margin: '0 0 1rem' }}>
              Changes to the company details below are saved on this call only. The client record stays as it is.
            </p>
          ) : null}
          {contactFields('edit-call')}
          {editCall?.status === 'Logged' ? (
            <div className="form-group">
              <label htmlFor="edit-call-spoke-with" className="form-label">
                Who did the employee talk to?
              </label>
              <input
                id="edit-call-spoke-with"
                type="text"
                className="form-input"
                value={spokeWith}
                onChange={(e) => setSpokeWith(e.target.value)}
                placeholder="Full name"
                disabled={submitting}
              />
            </div>
          ) : null}
          <div className="form-group">
            <label htmlFor="edit-call-at" className="form-label">
              {editCall?.status === 'Logged' ? 'When did the call occur?' : 'Date & time of the call'}
            </label>
            <input
              id="edit-call-at"
              type="datetime-local"
              className="form-input"
              value={callAt}
              onChange={(e) => {
                setCallAt(e.target.value);
                setFormError('');
              }}
              disabled={submitting}
            />
            {editCall?.status === 'Scheduled' ? (
              <p className="elevators-confirm-hint">
                The assigned employee gets an email reminder about an hour before the call.
              </p>
            ) : null}
          </div>
          <div className="form-group">
            <label htmlFor="edit-call-reason" className="form-label">
              Reason for the call
            </label>
            <textarea
              id="edit-call-reason"
              className="form-textarea"
              rows={2}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setFormError('');
              }}
              disabled={submitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="edit-call-notes" className="form-label">
              Notes
            </label>
            <textarea
              id="edit-call-notes"
              className="form-textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={submitting}
            />
            {editCall?.status === 'Logged' ? (
              <p className="elevators-confirm-hint">
                For updates on an ongoing case, use Notes in the Logged Calls table. Each follow-up note keeps
                its own date and author.
              </p>
            ) : null}
          </div>
          {errorBlock}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={submitting} onClick={() => setEditCall(null)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={submitting} onClick={() => void submitEdit()}>
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </Modal>

      {/* Follow-up notes on a logged call */}
      <Modal
        isOpen={notesCall != null}
        onClose={() => setNotesCall(null)}
        title="Call notes"
        wide
        contentClassName="call-log-modal"
      >
        <div className="modal-body">
          <dl className="call-log-summary">
            <div>
              <dt>Company</dt>
              <dd>{notesCall?.company_name}</dd>
            </div>
            <div>
              <dt>Employee</dt>
              <dd>{notesCall?.employee_name || '—'}</dd>
            </div>
            <div>
              <dt>Occurred</dt>
              <dd>{when(notesCall?.occurred_at ?? null)}</dd>
            </div>
            <div>
              <dt>Spoke with</dt>
              <dd>{notesCall?.spoke_with || '—'}</dd>
            </div>
            <div>
              <dt>Reason</dt>
              <dd>{notesCall?.reason || '—'}</dd>
            </div>
          </dl>
          <h3 className="call-log-notes-heading">Notes from the call</h3>
          <p className="call-log-note-body">{notesCall?.notes || '—'}</p>
          <h3 className="call-log-notes-heading">
            Follow-up notes{' '}
            {notesLoading ? null : <span className="events-section-count">({callNotes.length})</span>}
          </h3>
          {notesLoading ? (
            <p className="page-subtitle">Loading notes…</p>
          ) : callNotes.length === 0 ? (
            <p className="page-subtitle">No follow-up notes yet.</p>
          ) : (
            <ul className="call-log-note-list">
              {callNotes.map((n) => (
                <li className="call-log-note-item" key={n.id}>
                  <div className="call-log-note-meta">
                    <strong>{n.submitted_by || 'Someone'}</strong>
                    <span>{when(n.created_at)}</span>
                  </div>
                  <p className="call-log-note-body">{n.note}</p>
                </li>
              ))}
            </ul>
          )}
          <div className="form-group">
            <label htmlFor="call-note-draft" className="form-label">
              Add a note
            </label>
            <textarea
              id="call-note-draft"
              className="form-textarea"
              rows={3}
              value={noteDraft}
              onChange={(e) => {
                setNoteDraft(e.target.value);
                setFormError('');
              }}
              placeholder="e.g. Called back, replacement part is on order"
              disabled={submitting}
            />
          </div>
          {errorBlock}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={submitting} onClick={() => setNotesCall(null)}>
            Close
          </button>
          <button
            type="button"
            className="update-button"
            disabled={submitting || notesLoading || !noteDraft.trim()}
            onClick={() => void submitNote()}
          >
            {submitting ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default CallLogTab;
